import {
  BarChart3,
  CheckSquare,
  ClipboardCopy,
  Edit3,
  LockKeyhole,
  RefreshCw,
  Save,
  Search,
  Square,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import {
  clearInteractionLogs,
  copyLogsToTrainingExamples,
  getInteractionLogs,
  updateInteractionLog,
} from "../lib/documentSearch/documentSearchClient";
import type {
  InteractionEventType,
  InteractionLogBucket,
  InteractionLogRecord,
  InteractionLogsResponse,
  SearchFilters,
  SearchMode,
} from "../lib/documentSearch/types";

type DraftState = {
  query: string;
  resultMode: SearchMode | "unknown" | "";
  searchQuery: string;
  answer: string;
  notes: string;
  evalLabel: string;
  expectedSearchQuery: string;
  expectedFiltersText: string;
};

const PASSWORD_STORAGE_KEY = "mydigigovsa-log-password";
const EVENT_FILTERS: Array<InteractionEventType | ""> = [
  "",
  "search",
  "search_error",
  "source_open",
  "source_pin",
  "source_unpin",
  "chat_turn",
  "chat_error",
];
const MODE_FILTERS: Array<SearchMode | "unknown" | ""> = [
  "",
  "ai-search",
  "manifest-fallback",
  "local-mock",
  "unknown",
];
const LABELS = [
  "",
  "good_training_example",
  "needs_adapter_fix",
  "bad_source",
  "irrelevant",
  "needs_review",
];

export function InteractionLogsPanel() {
  const [password, setPassword] = useState(() => {
    try {
      return window.sessionStorage.getItem(PASSWORD_STORAGE_KEY) ?? "";
    } catch {
      return "";
    }
  });
  const [unlocked, setUnlocked] = useState(false);
  const [response, setResponse] = useState<InteractionLogsResponse | null>(null);
  const [eventFilter, setEventFilter] = useState<InteractionEventType | "">("");
  const [modeFilter, setModeFilter] = useState<SearchMode | "unknown" | "">("");
  const [queryFilter, setQueryFilter] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeLogId, setActiveLogId] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmingClear, setConfirmingClear] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const logs = response?.logs ?? [];
  const activeLog = useMemo(
    () => logs.find((log) => log.id === activeLogId) ?? logs[0] ?? null,
    [activeLogId, logs],
  );

  useEffect(() => {
    if (!activeLog) {
      setDraft(null);
      return;
    }
    setActiveLogId(activeLog.id);
    setDraft(draftFromLog(activeLog));
  }, [activeLog?.id]);

  async function loadLogs(nextPassword = password) {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const nextResponse = await getInteractionLogs(nextPassword, {
        eventType: eventFilter || undefined,
        resultMode: modeFilter || undefined,
        query: queryFilter.trim() || undefined,
        limit: 100,
      });
      setResponse(nextResponse);
      setUnlocked(true);
      setSelectedIds(new Set());
      setConfirmingClear(false);
      try {
        window.sessionStorage.setItem(PASSWORD_STORAGE_KEY, nextPassword);
      } catch {
        // Ignore private browsing storage failures.
      }
    } catch (loadError) {
      setUnlocked(false);
      setResponse(null);
      setError(loadError instanceof Error ? loadError.message : "Unable to load logs");
    } finally {
      setLoading(false);
    }
  }

  function handleUnlock(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void loadLogs();
  }

  function toggleSelected(id: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  async function handleSave() {
    if (!activeLog || !draft) {
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const expectedFilters = parseFiltersDraft(draft.expectedFiltersText);
      const updated = await updateInteractionLog(password, activeLog.id, {
        query: draft.query,
        resultMode: draft.resultMode || undefined,
        searchQuery: draft.searchQuery,
        answer: draft.answer,
        notes: draft.notes,
        evalLabel: draft.evalLabel,
        expectedSearchQuery: draft.expectedSearchQuery,
        expectedFilters,
      });
      setResponse((current) =>
        current
          ? {
              ...current,
              logs: current.logs.map((log) => (log.id === updated.id ? updated : log)),
            }
          : current,
      );
      setMessage("Log saved.");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to save log");
    } finally {
      setSaving(false);
    }
  }

  async function handleCopySelected() {
    const ids = [...selectedIds];
    if (ids.length === 0) {
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const copyResponse = await copyLogsToTrainingExamples(password, ids);
      setMessage(`${copyResponse.copied} training example${copyResponse.copied === 1 ? "" : "s"} copied.`);
      await loadLogs();
    } catch (copyError) {
      setError(copyError instanceof Error ? copyError.message : "Unable to copy logs");
    } finally {
      setSaving(false);
    }
  }

  async function handleClearRawLogs() {
    if (!confirmingClear) {
      setConfirmingClear(true);
      setError("");
      setMessage("Click confirm clear to remove raw logs. Training examples will be kept.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");
    try {
      const clearResponse = await clearInteractionLogs(password);
      const nextResponse = await getInteractionLogs(password, {
        eventType: eventFilter || undefined,
        resultMode: modeFilter || undefined,
        query: queryFilter.trim() || undefined,
        limit: 100,
      });
      setResponse(nextResponse);
      setSelectedIds(new Set());
      setActiveLogId(null);
      setDraft(null);
      setConfirmingClear(false);
      setMessage(
        `${clearResponse.deleted} raw log record${
          clearResponse.deleted === 1 ? "" : "s"
        } removed. Training examples were kept.`,
      );
    } catch (clearError) {
      setError(clearError instanceof Error ? clearError.message : "Unable to clear logs");
    } finally {
      setSaving(false);
    }
  }

  if (!unlocked) {
    return (
      <section className="interaction-logs interaction-logs--locked">
        <form className="log-access" onSubmit={handleUnlock}>
          <div className="log-access__icon" aria-hidden="true">
            <LockKeyhole size={24} />
          </div>
          <div>
            <p className="section-kicker">Evaluation</p>
            <h2>Interaction logs</h2>
          </div>
          <label>
            <span>Password</span>
            <input
              autoComplete="current-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          <button className="primary-button" disabled={!password || loading} type="submit">
            <LockKeyhole size={18} aria-hidden="true" />
            <span>{loading ? "Checking" : "Unlock"}</span>
          </button>
          {error ? <p className="form-error">{error}</p> : null}
        </form>
      </section>
    );
  }

  return (
    <section className="interaction-logs" aria-label="Interaction logs">
      <div className="interaction-logs__toolbar">
        <div>
          <p className="section-kicker">Evaluation</p>
          <h2>Interaction logs</h2>
        </div>
        <div className="interaction-logs__actions">
          <button
            className="secondary-button"
            type="button"
            disabled={saving || selectedIds.size === 0}
            onClick={() => void handleCopySelected()}
          >
            <ClipboardCopy size={18} aria-hidden="true" />
            <span>Copy selected</span>
          </button>
          <button
            className="secondary-button"
            type="button"
            disabled={loading}
            onClick={() => void loadLogs()}
          >
            <RefreshCw size={18} aria-hidden="true" />
            <span>Refresh</span>
          </button>
          <button
            className={[
              "secondary-button",
              "secondary-button--danger",
              confirmingClear ? "is-confirming" : "",
            ]
              .filter(Boolean)
              .join(" ")}
            type="button"
            disabled={saving || loading || !response || response.stats.total === 0}
            onClick={() => void handleClearRawLogs()}
          >
            <Trash2 size={18} aria-hidden="true" />
            <span>{confirmingClear ? "Confirm clear" : "Clear raw logs"}</span>
          </button>
        </div>
      </div>

      <div className="log-filters">
        <label>
          <span>Event</span>
          <select
            value={eventFilter}
            onChange={(event) => setEventFilter(event.target.value as InteractionEventType | "")}
          >
            {EVENT_FILTERS.map((eventType) => (
              <option key={eventType || "all"} value={eventType}>
                {eventType || "All events"}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Mode</span>
          <select
            value={modeFilter}
            onChange={(event) => setModeFilter(event.target.value as SearchMode | "unknown" | "")}
          >
            {MODE_FILTERS.map((mode) => (
              <option key={mode || "all"} value={mode}>
                {mode || "All modes"}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Find</span>
          <div className="log-search-input">
            <Search size={16} aria-hidden="true" />
            <input
              type="search"
              value={queryFilter}
              onChange={(event) => setQueryFilter(event.target.value)}
            />
          </div>
        </label>
        <button className="primary-button" type="button" onClick={() => void loadLogs()}>
          <Search size={18} aria-hidden="true" />
          <span>Apply</span>
        </button>
      </div>

      {response ? (
        <LogStats stats={response.stats} />
      ) : null}

      <div className="interaction-logs__body">
        <div className="log-table-shell">
          <div className="log-table">
            <div className="log-table__head">
              <span />
              <span>Time</span>
              <span>Event</span>
              <span>Mode</span>
              <span>Query or source</span>
              <span>Results</span>
            </div>
            {logs.map((log) => (
              <button
                key={log.id}
                type="button"
                className={[
                  "log-row",
                  activeLog?.id === log.id ? "is-active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setActiveLogId(log.id)}
              >
                <span
                  className="log-row__checkbox"
                  role="checkbox"
                  aria-checked={selectedIds.has(log.id)}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleSelected(log.id);
                  }}
                >
                  {selectedIds.has(log.id) ? (
                    <CheckSquare size={17} aria-hidden="true" />
                  ) : (
                    <Square size={17} aria-hidden="true" />
                  )}
                </span>
                <span>{formatDateTime(log.createdAt)}</span>
                <span>{log.eventType}</span>
                <span>{log.resultMode ?? "unknown"}</span>
                <strong>{primaryLogText(log)}</strong>
                <span>{log.totalResults ?? "-"}</span>
              </button>
            ))}
            {logs.length === 0 ? (
              <div className="log-table__empty">No log records found.</div>
            ) : null}
          </div>
        </div>

        <aside className="log-editor">
          <div className="log-editor__header">
            <div>
              <p className="section-kicker">Record</p>
              <h3>{activeLog ? activeLog.eventType : "No selection"}</h3>
            </div>
            <Edit3 size={18} aria-hidden="true" />
          </div>

          {activeLog && draft ? (
            <>
              <dl className="log-detail-grid">
                <div>
                  <dt>ID</dt>
                  <dd>{activeLog.id}</dd>
                </div>
                <div>
                  <dt>Session</dt>
                  <dd>{activeLog.sessionId}</dd>
                </div>
                <div>
                  <dt>Source</dt>
                  <dd>{activeLog.sourceDocumentId ?? "-"}</dd>
                </div>
                <div>
                  <dt>Duration</dt>
                  <dd>{activeLog.durationMs ? `${activeLog.durationMs} ms` : "-"}</dd>
                </div>
              </dl>

              <RouterTracePanel log={activeLog} />

              <div className="log-editor__fields">
                <label>
                  <span>Query</span>
                  <input
                    value={draft.query}
                    onChange={(event) =>
                      setDraft((current) =>
                        current ? { ...current, query: event.target.value } : current,
                      )
                    }
                  />
                </label>
                <label>
                  <span>Result mode</span>
                  <select
                    value={draft.resultMode}
                    onChange={(event) =>
                      setDraft((current) =>
                        current
                          ? {
                              ...current,
                              resultMode: event.target.value as DraftState["resultMode"],
                            }
                          : current,
                      )
                    }
                  >
                    {MODE_FILTERS.map((mode) => (
                      <option key={mode || "empty"} value={mode}>
                        {mode || "Unset"}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  <span>Observed search query</span>
                  <input
                    value={draft.searchQuery}
                    onChange={(event) =>
                      setDraft((current) =>
                        current ? { ...current, searchQuery: event.target.value } : current,
                      )
                    }
                  />
                </label>
                <label>
                  <span>Expected search query</span>
                  <input
                    value={draft.expectedSearchQuery}
                    onChange={(event) =>
                      setDraft((current) =>
                        current
                          ? { ...current, expectedSearchQuery: event.target.value }
                          : current,
                      )
                    }
                  />
                </label>
                <label>
                  <span>Label</span>
                  <select
                    value={draft.evalLabel}
                    onChange={(event) =>
                      setDraft((current) =>
                        current ? { ...current, evalLabel: event.target.value } : current,
                      )
                    }
                  >
                    {LABELS.map((label) => (
                      <option key={label || "empty"} value={label}>
                        {label || "Unlabeled"}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="log-editor__field--wide">
                  <span>Expected filters JSON</span>
                  <textarea
                    rows={5}
                    value={draft.expectedFiltersText}
                    onChange={(event) =>
                      setDraft((current) =>
                        current
                          ? { ...current, expectedFiltersText: event.target.value }
                          : current,
                      )
                    }
                  />
                </label>
                <label className="log-editor__field--wide">
                  <span>Answer</span>
                  <textarea
                    rows={4}
                    value={draft.answer}
                    onChange={(event) =>
                      setDraft((current) =>
                        current ? { ...current, answer: event.target.value } : current,
                      )
                    }
                  />
                </label>
                <label className="log-editor__field--wide">
                  <span>Notes</span>
                  <textarea
                    rows={4}
                    value={draft.notes}
                    onChange={(event) =>
                      setDraft((current) =>
                        current ? { ...current, notes: event.target.value } : current,
                      )
                    }
                  />
                </label>
              </div>

              <button
                className="primary-button primary-button--full"
                type="button"
                disabled={saving}
                onClick={() => void handleSave()}
              >
                <Save size={18} aria-hidden="true" />
                <span>{saving ? "Saving" : "Save log"}</span>
              </button>

              <details className="log-payload">
                <summary>Payload</summary>
                <pre>{JSON.stringify(payloadWithoutTokenUsage(activeLog.payload), null, 2)}</pre>
              </details>
            </>
          ) : (
            <p className="log-table__empty">Select a log record.</p>
          )}
        </aside>
      </div>

      {message ? <p className="form-success">{message}</p> : null}
      {error ? <p className="form-error">{error}</p> : null}
    </section>
  );
}

function LogStats({ stats }: { stats: InteractionLogsResponse["stats"] }) {
  return (
    <div className="log-stats">
      <div className="log-stat-card">
        <BarChart3 size={19} aria-hidden="true" />
        <span>Total logs</span>
        <strong>{stats.total}</strong>
      </div>
      <div className="log-stat-card">
        <ClipboardCopy size={19} aria-hidden="true" />
        <span>Training examples</span>
        <strong>{stats.trainingExampleCount}</strong>
      </div>
      <BarGroup title="Events" buckets={stats.byEvent} />
      <BarGroup title="Result mode" buckets={stats.byMode} />
      <Timeline buckets={stats.timeline} />
    </div>
  );
}

function RouterTracePanel({ log }: { log: InteractionLogRecord }) {
  const routing = routerTraceFromLog(log);
  if (!routing) {
    return null;
  }
  return (
    <section className="log-router-trace" aria-label="Gemma LoRA adapter output">
      <div>
        <p className="section-kicker">Gemma LoRA Adapter</p>
        <h4>{routing.used ? "Adapter output captured" : "Adapter not used"}</h4>
      </div>
      <dl className="log-router-meta">
        <div>
          <dt>Model</dt>
          <dd>{routing.model || "-"}</dd>
        </div>
        <div>
          <dt>LoRA</dt>
          <dd>{routing.loraName || "-"}</dd>
        </div>
        <div>
          <dt>Effective query</dt>
          <dd>{routing.effectiveQuery || "-"}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>{routing.error ? routing.error : routing.used ? "used" : "not used"}</dd>
        </div>
      </dl>
      {routing.output ? (
        <div className="log-router-json">
          <div>
            <strong>Parsed output</strong>
            <pre>{JSON.stringify(routing.output, null, 2)}</pre>
          </div>
          <div>
            <strong>Effective filters</strong>
            <pre>{JSON.stringify(routing.effectiveFilters ?? {}, null, 2)}</pre>
          </div>
        </div>
      ) : null}
      {routing.rawText ? (
        <details className="log-payload" open>
          <summary>Raw adapter text</summary>
          <pre>{routing.rawText}</pre>
        </details>
      ) : null}
    </section>
  );
}

function BarGroup({
  title,
  buckets,
}: {
  title: string;
  buckets: InteractionLogBucket[];
}) {
  const max = Math.max(1, ...buckets.map((bucket) => bucket.count));
  return (
    <div className="log-chart">
      <h3>{title}</h3>
      {buckets.slice(0, 6).map((bucket) => (
        <div className="log-bar" key={bucket.key}>
          <span>{bucket.key}</span>
          <div>
            <i style={{ width: `${Math.max(8, (bucket.count / max) * 100)}%` }} />
          </div>
          <strong>{bucket.count}</strong>
        </div>
      ))}
    </div>
  );
}

function Timeline({ buckets }: { buckets: InteractionLogBucket[] }) {
  const max = Math.max(1, ...buckets.map((bucket) => bucket.count));
  return (
    <div className="log-chart log-chart--timeline">
      <h3>Timeline</h3>
      <div className="log-timeline">
        {buckets.map((bucket) => (
          <span
            key={bucket.key}
            title={`${bucket.key}: ${bucket.count}`}
            style={{ height: `${Math.max(10, (bucket.count / max) * 100)}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function draftFromLog(log: InteractionLogRecord): DraftState {
  return {
    query: log.query ?? "",
    resultMode: log.resultMode ?? "",
    searchQuery: log.searchQuery ?? "",
    answer: log.answer ?? "",
    notes: log.notes ?? "",
    evalLabel: log.evalLabel ?? "",
    expectedSearchQuery: log.expectedSearchQuery ?? log.searchQuery ?? log.query ?? "",
    expectedFiltersText: JSON.stringify(
      log.expectedFilters ?? log.filters ?? emptyFilters(),
      null,
      2,
    ),
  };
}

function primaryLogText(log: InteractionLogRecord): string {
  if (log.eventType === "source_open" || log.eventType === "source_pin" || log.eventType === "source_unpin") {
    return log.sourceTitle || log.sourceDocumentId || log.query || "No source";
  }
  return log.query || log.sourceTitle || log.sourceDocumentId || "No query";
}

function finiteNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function payloadWithoutTokenUsage(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(payloadWithoutTokenUsage);
  }
  if (!isRecord(value)) {
    return value;
  }
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => {
        const normalized = key.toLowerCase();
        return normalized !== "usage" && normalized !== "threadusage" && !normalized.includes("token");
      })
      .map(([key, nestedValue]) => [key, payloadWithoutTokenUsage(nestedValue)]),
  );
}

function routerTraceFromLog(log: InteractionLogRecord):
  | {
      model?: string;
      loraName?: string;
      used?: boolean;
      rawText?: string;
      output?: unknown;
      effectiveQuery?: string;
      effectiveFilters?: unknown;
      error?: string;
    }
  | null {
  const routing = log.payload?.routing;
  if (!isRecord(routing)) {
    return null;
  }
  const adapter = isRecord(routing.adapter) ? routing.adapter : {};
  return {
    model: stringValue(adapter.model),
    loraName: stringValue(adapter.loraName),
    used: booleanValue(adapter.used),
    rawText: stringValue(routing.rawText),
    output: routing.output,
    effectiveQuery: stringValue(routing.effectiveQuery),
    effectiveFilters: routing.effectiveFilters,
    error: stringValue(routing.error),
  };
}

function parseFiltersDraft(value: string): SearchFilters | undefined {
  if (!value.trim()) {
    return undefined;
  }
  const parsed = JSON.parse(value) as Partial<SearchFilters>;
  return {
    collections: stringArray(parsed.collections),
    documentTypes: stringArray(parsed.documentTypes) as SearchFilters["documentTypes"],
    years: numberArray(parsed.years),
    tags: stringArray(parsed.tags),
  };
}

function emptyFilters(): SearchFilters {
  return {
    collections: [],
    documentTypes: [],
    years: [],
    tags: [],
  };
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}

function numberArray(value: unknown): number[] {
  return Array.isArray(value)
    ? value
        .map((item) => (typeof item === "number" ? item : Number(item)))
        .filter((item) => Number.isInteger(item))
    : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function booleanValue(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
